import { cx } from "src/lib/utils/cx";

const sizes = {
    xs: "size-4",
    sm: "size-5",
    md: "size-6",
    lg: "size-8",
    xl: "size-10",
} as const;

export type LoadingIndicatorSize = keyof typeof sizes;
export type LoadingIndicatorType = "line-simple" | "line-spinner" | "dot-circle";

export interface LoadingIndicatorProps {
    /** The size of the loading indicator. */
    size?: LoadingIndicatorSize;
    /** The visual style of the loading indicator. */
    type?: LoadingIndicatorType;
    /** Accessible label announced to screen readers. */
    label?: string;
    /** Additional CSS classes. */
    className?: string;
}

/**
 * LoadingIndicator — indicador de carregamento padrão (Untitled UI).
 *
 * Usa `currentColor`, então a cor pode ser controlada via classe utilitária
 * (ex.: `text-brand-600`). O padrão é a cor de marca secundária.
 */
export const LoadingIndicator = ({
    size = "sm",
    type = "line-simple",
    label = "Carregando",
    className,
}: LoadingIndicatorProps) => {
    const rootClassName = cx("text-brand-secondary", sizes[size], className);

    if (type === "dot-circle") {
        // Anel de 8 pontos com opacidade graduada girando — o "dot-circle".
        const dots = Array.from({ length: 8 });
        return (
            <svg
                role="status"
                aria-label={label}
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={cx("animate-spin", rootClassName)}
            >
                {dots.map((_, index) => (
                    <circle
                        key={index}
                        cx="12"
                        cy="3.25"
                        r="2.25"
                        fill="currentColor"
                        opacity={(index + 1) / dots.length}
                        transform={`rotate(${(index * 360) / dots.length} 12 12)`}
                    />
                ))}
            </svg>
        );
    }

    // line-simple / line-spinner — anel circular girando.
    return (
        <svg
            role="status"
            aria-label={label}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={cx("animate-spin", rootClassName)}
        >
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
            <path
                d="M21 12a9 9 0 0 0-9-9"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
            />
        </svg>
    );
};

export default LoadingIndicator;
